// M15 actions behind the AI buttons. Each one sends a small rendering of the photo to the provider the user
// chose, only when the user presses the button, and only with a key set (aiClient throws 'no-key' before any
// request otherwise).
import { create } from 'zustand';
import { useLibrary, type LibraryPhoto } from '../../state/library';
import { useEditor } from '../../state/editor';
import { defaultSettings } from '../../model/defaults';
import { exportWithExtras } from '../exportHelpers';
import type { Settings } from '../../model/types';
import { AiError, adText, aiImage, smartFix, type SmartFix } from './aiClient';
import { translate, useLang, type MessageKey } from '../../i18n';
import { useDesign } from '../../design/state';
import { setAd, imageNodes } from '../../design/templates/instantiate';
import { getAsset } from '../../design/assets';
import { sanitizeName } from '../../io/rename';
import { markAiGenerated } from './aiMarker';

type AiUi = {
  busy: null | 'smartFix' | 'touchup' | 'upscale' | 'adText';
  error: string | null;
  /** The last AI image result, shown beside the original (never applied to the photo). */
  compare: null | {
    kind: 'touchup' | 'upscale';
    photoName: string;
    original: string;
    result: string;
    bytes: Uint8Array;
    mime: string;
    width: number;
    height: number;
  };
  settingsOpen: boolean;
};
export const useAiUi = create<AiUi>(() => ({ busy: null, error: null, compare: null, settingsOpen: false }));

const active = (): LibraryPhoto | undefined => {
  const l = useLibrary.getState();
  return l.photos.find((p) => p.id === l.activeId);
};

async function jpegOf(p: LibraryPhoto, s: Settings, edge: number): Promise<Uint8Array> {
  const r = await exportWithExtras(
    p,
    s,
    {
      format: 'jpeg',
      bitDepth: 8,
      quality: 85,
      size: { mode: 'longEdge', w: edge },
      dpi: 72,
      colorSpace: 'srgb',
      sharpen: { target: 'none', amount: 'standard' },
      background: 'asEdited',
      metadata: 'stripAll',
    },
    {},
  );
  return r.bytes;
}

const ERROR_TEXT: Record<AiError['code'], MessageKey> = {
  'no-key': 'aiErrNoKey',
  'bad-key': 'aiErrBadKey',
  quota: 'aiErrQuota',
  network: 'aiErrNetwork',
  timeout: 'aiErrTimeout',
  'bad-answer': 'aiErrBadAnswer',
  refused: 'aiErrRefused',
  cancelled: 'aiErrCancelled',
};

/** The reason shown to the user, in the UI language (the English AiError message is for debugging only). */
export function aiErrorText(e: unknown): string {
  const lang = useLang.getState().lang;
  if (e instanceof AiError) return translate(lang, ERROR_TEXT[e.code]);
  return (e as Error)?.message || String(e);
}

let running: AbortController | null = null;

/** Cancel [Cancelar] (Your call Y1): abandons the running AI request; nothing is applied and no error shows. */
export function cancelAi() {
  running?.abort();
}

/** Throws 'cancelled' once Cancel was pressed, so a result that arrives late is never applied. */
function checkCancel(signal: AbortSignal) {
  if (signal.aborted) throw new AiError('cancelled', 'Cancelled by the user');
}

async function guard<T>(
  busy: NonNullable<AiUi['busy']>,
  fn: (signal: AbortSignal) => Promise<T>,
): Promise<T | null> {
  const ctl = new AbortController();
  running = ctl;
  useAiUi.setState({ busy, error: null });
  try {
    const r = await fn(ctl.signal);
    checkCancel(ctl.signal);
    return r;
  } catch (e) {
    if (!ctl.signal.aborted) useAiUi.setState({ error: aiErrorText(e) });
    return null;
  } finally {
    if (running === ctl) running = null;
    useAiUi.setState({ busy: null });
  }
}

/** Applies smart-fix values to settings (absolute values for the sliders it covers). */
export function applySmartFix(s: Settings, f: SmartFix) {
  s.light = {
    exposure: f.exposure,
    contrast: f.contrast,
    highlights: f.highlights,
    shadows: f.shadows,
    whites: f.whites,
    blacks: f.blacks,
  };
  s.wb = { temp: f.temp, tint: f.tint };
  s.color = { vibrance: f.vibrance, saturation: f.saturation };
  s.cleanWhites = { ...s.cleanWhites, amount: f.cleanWhites };
}

/**
 * (a) Smart fix: a ≤ 768 px JPEG of the photo as shot (crop kept, other edits off) → slider values → one undo
 * step. Never generated pixels.
 */
export async function smartFixActive(): Promise<SmartFix | null> {
  const p = active();
  if (!p) return null;
  return guard('smartFix', async (signal) => {
    const cur = useEditor.getState().get(p.id);
    const neutral = { ...defaultSettings(), geometry: cur.geometry, upscale: cur.upscale };
    const fix = await smartFix(await jpegOf(p, neutral, 768), signal);
    checkCancel(signal);
    useEditor.getState().update(p.id, (s) => applySmartFix(s, fix));
    return fix;
  });
}

/** (c) AI touch-up / upscale of the active photo with its current edits: shown beside the original only. */
export async function aiImageActive(kind: 'touchup' | 'upscale'): Promise<void> {
  const p = active();
  if (!p) return;
  await guard(kind, async (signal) => {
    const s = useEditor.getState().get(p.id);
    const src = await jpegOf(p, s, 1536);
    const out = await aiImage(kind, { bytes: src, mime: 'image/jpeg' }, signal);
    const bmp = await createImageBitmap(new Blob([out.bytes as Uint8Array<ArrayBuffer>], { type: out.mime }));
    if (signal.aborted) {
      bmp.close();
      checkCancel(signal);
    }
    const prev = useAiUi.getState().compare;
    if (prev) {
      URL.revokeObjectURL(prev.original);
      URL.revokeObjectURL(prev.result);
    }
    useAiUi.setState({
      compare: {
        kind,
        photoName: p.name,
        original: URL.createObjectURL(new Blob([src as Uint8Array<ArrayBuffer>], { type: 'image/jpeg' })),
        result: URL.createObjectURL(new Blob([out.bytes as Uint8Array<ArrayBuffer>], { type: out.mime })),
        bytes: out.bytes,
        mime: out.mime,
        width: bmp.width,
        height: bmp.height,
      },
    });
    bmp.close();
  });
}

/**
 * Saves the AI result as a NEW file (exports/ai/…, or a download); the photo itself is never replaced. The file
 * carries the IPTC "AI-generated" marker as well as its `_ai-` name (Your call Y10).
 */
export async function saveAiResult(): Promise<string | null> {
  const c = useAiUi.getState().compare;
  if (!c) return null;
  const ext = c.mime.includes('png') ? 'png' : 'jpg';
  const name = `${sanitizeName(c.photoName.replace(/\.[^.]+$/, '')) || 'photo'}_ai-${c.kind}.${ext}`;
  const bytes = markAiGenerated(c.bytes, c.kind);
  const folder = useLibrary.getState().folder;
  if (folder?.mode === 'fsa') return folder.writeExport('ai', name, bytes);
  const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: c.mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}

/**
 * A slot image is stored as the file the user dropped (EXIF and GPS included, full size). Like smart fix, the
 * provider only gets a ≤ 768 px JPEG re-encoded by the canvas, which carries no metadata.
 */
export async function smallJpeg(
  bytes: Uint8Array,
  type: string,
  edge = 768,
): Promise<{ bytes: Uint8Array; mime: string }> {
  const bmp = await createImageBitmap(new Blob([bytes as Uint8Array<ArrayBuffer>], { type }));
  try {
    const k = Math.min(1, edge / Math.max(bmp.width, bmp.height));
    const c = new OffscreenCanvas(
      Math.max(1, Math.round(bmp.width * k)),
      Math.max(1, Math.round(bmp.height * k)),
    );
    const g = c.getContext('2d')!;
    g.fillStyle = '#fff'; // a transparent PNG logo-style photo would otherwise turn black in JPEG
    g.fillRect(0, 0, c.width, c.height);
    g.drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await c.convertToBlob({ type: 'image/jpeg', quality: 0.85 });
    return { bytes: new Uint8Array(await blob.arrayBuffer()), mime: 'image/jpeg' };
  } finally {
    bmp.close();
  }
}

/** (b) Ad text: from the ad's photo and the dish name typed so far → the form's fields (one undo step). */
export async function adTextForAd(): Promise<void> {
  const doc = useDesign.getState().doc;
  if (!doc?.ad) return;
  await guard('adText', async (signal) => {
    const slot = imageNodes(doc, 'photo').find((n) => n.assetId);
    const asset = slot?.assetId ? getAsset(slot.assetId) : undefined;
    const r = await adText({
      dishName: doc.ad!.dishName.es || doc.ad!.dishName.en,
      photo: asset ? await smallJpeg(asset.bytes, asset.type) : undefined,
      signal,
    });
    checkCancel(signal);
    useDesign.getState().run(
      setAd((a) => {
        a.dishName = r.dishName;
        a.description = r.description;
        a.headline = r.promo;
      }),
    );
    useDesign.getState().endGesture();
  });
}
